<template>
  <div class="tracks-map-view">
    <div class="tracks-map-mobile-tabs dashboard-tab-bar" role="tablist" aria-label="Вид треков на карте">
      <button
        v-for="tab in mobileTabs"
        :key="tab.id"
        type="button"
        class="dashboard-tab"
        :class="{ 'dashboard-tab--active': mobileTab === tab.id }"
        role="tab"
        :aria-selected="mobileTab === tab.id"
        @click="mobileTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="tracks-map-layout">
      <aside
        v-show="!isMobile || mobileTab === 'list'"
        class="tracks-map-sidebar panel"
      >
        <div class="tracks-map-filters">
          <div class="tracks-map-filter-row">
            <span class="tracks-map-filter-label">Публичные треки</span>
            <ColumnFilter
              v-model="sort"
              :options="sortOptions"
              :loading="loading"
            />
          </div>
          <TrackDifficultyFilter v-model="difficultyFilter" />
        </div>

        <div class="tracks-map-list-wrap">
          <p v-if="loading" class="empty-state">Загрузка...</p>
          <p v-else-if="error" class="error-message">{{ error }}</p>
          <p v-else-if="!tracks.length" class="empty-state">{{ emptyText }}</p>
          <template v-else>
            <label class="tracks-map-visibility-bar">
              <input
                type="checkbox"
                :checked="allVisible"
                :indeterminate="someVisible && !allVisible"
                @change="toggleAllVisibility"
              />
              <span>Показать все на карте</span>
            </label>

            <ul class="track-list tracks-map-list">
              <TrackMapListItem
                v-for="track in tracks"
                :key="track.id"
                :track="track"
                :active="track.id === selectedTrackId"
                :visible="visibleTrackIds.has(track.id)"
                @select="selectTrack"
                @toggle-visibility="toggleTrackVisibility"
              />
            </ul>
          </template>
        </div>

        <ListPager
          :page="page"
          :total-pages="pagination?.total_pages || 0"
          :total="pagination?.total || 0"
          :disabled="loading"
          @update:page="page = $event"
        />
      </aside>

      <section
        v-if="!isMobile || mobileTab === 'map'"
        class="tracks-map-panel"
        aria-label="Карта публичных треков"
      >
        <TrackMap
          :tracks="visibleTracks"
          :selected-track-id="selectedTrackId"
          :show-endpoints="false"
        />
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import ColumnFilter from './ColumnFilter.vue'
import ListPager from './ListPager.vue'
import TrackDifficultyFilter from './TrackDifficultyFilter.vue'
import TrackMap from './TrackMap.vue'
import TrackMapListItem from './TrackMapListItem.vue'
import { fetchPublicTracks } from '../api/tracks'
import { useMediaQuery } from '../composables/useMediaQuery'
import { parsePaginatedList } from '../utils/pagination'

const PAGE_SIZE = 10

const isMobile = useMediaQuery('(max-width: 767px)')

const tracks = ref([])
const pagination = ref(null)
const page = ref(1)
const sort = ref('new')
const loading = ref(false)
const error = ref('')
const selectedTrackId = ref(null)
const visibleTrackIds = ref(new Set())
const difficultyFilter = ref({ season: 'summer', wheelSize: '' })
const mobileTab = ref('list')

const mobileTabs = [
  { id: 'list', label: 'Список' },
  { id: 'map', label: 'Карта' },
]

const sortOptions = [
  { value: 'new', label: 'Новые на сайте' },
  { value: 'most_commented', label: 'Самые обсуждаемые' },
  { value: 'most_liked', label: 'Самые популярные' },
  { value: 'most_completed', label: 'Самые изъезженные' },
]

const emptyText = computed(() => {
  if (difficultyFilter.value.wheelSize) return 'Нет треков с выбранной сложностью.'
  return 'Пока нет публичных треков.'
})

const visibleTracks = computed(() => {
  return tracks.value.filter((track) => visibleTrackIds.value.has(track.id))
})

const allVisible = computed(() => {
  return tracks.value.length > 0 && tracks.value.every((track) => visibleTrackIds.value.has(track.id))
})

const someVisible = computed(() => {
  return tracks.value.some((track) => visibleTrackIds.value.has(track.id))
})

function difficultyParams() {
  return {
    season: difficultyFilter.value.season,
    wheelSize: difficultyFilter.value.wheelSize || undefined,
  }
}

async function loadTracks() {
  loading.value = true
  error.value = ''

  try {
    const response = await fetchPublicTracks({
      sort: sort.value,
      page: page.value,
      perPage: PAGE_SIZE,
      ...difficultyParams(),
    })
    const { items, pagination: nextPagination } = parsePaginatedList(response, 'tracks')
    tracks.value = items
    pagination.value = nextPagination
    visibleTrackIds.value = new Set(items.map((track) => track.id))

    if (selectedTrackId.value && !items.some((track) => track.id === selectedTrackId.value)) {
      selectedTrackId.value = null
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось загрузить публичные треки.'
  } finally {
    loading.value = false
  }
}

function resetAndLoadTracks() {
  if (page.value === 1) {
    loadTracks()
    return
  }

  page.value = 1
}

function selectTrack(trackId) {
  selectedTrackId.value = trackId
  if (isMobile.value) {
    mobileTab.value = 'map'
  }
}

function toggleTrackVisibility(trackId) {
  const nextVisibleIds = new Set(visibleTrackIds.value)

  if (nextVisibleIds.has(trackId)) {
    nextVisibleIds.delete(trackId)
  } else {
    nextVisibleIds.add(trackId)
  }

  visibleTrackIds.value = nextVisibleIds
}

function toggleAllVisibility() {
  visibleTrackIds.value = allVisible.value
    ? new Set()
    : new Set(tracks.value.map((track) => track.id))
}

watch(page, loadTracks)
watch(sort, resetAndLoadTracks)
watch(difficultyFilter, resetAndLoadTracks, { deep: true })

onMounted(loadTracks)
</script>
